import { Routes, Route, Outlet } from 'react-router-dom';
import { lazy, Suspense, type ReactNode } from 'react';
import { CrtOverlay } from './components/CrtOverlay';
import { useActivityWebSocket } from './hooks/useActivityWebSocket';
// The gated pages drag in Phaser and xterm — ~1.9 MB that a visitor on the
// landing page never runs. Split behind React.lazy so `/` stops shipping the
// game engine, and so a marketing render never has to boot a WebGL canvas.
const HomePage = lazy(() => import('./pages/HomePage'));
const BunkerPage = lazy(() => import('./pages/BunkerPage'));
import LandingPage from './pages/LandingPage';
import PricingPage from './pages/PricingPage';
import ProofPage from './pages/ProofPage';
import { AuthProvider, useAuth } from './hooks/useAuth.tsx';
import { LoginScreen } from './components/Auth/LoginScreen.tsx';
import FeedbackButton from './components/FeedbackButton';

/** True when auth should be bypassed. An explicit VITE_AUTH_DISABLED=false wins
 *  (used by tests); otherwise DEV bypasses, matching prior behavior. */
function authBypassed(): boolean {
  if (import.meta.env.VITE_AUTH_DISABLED === 'true') return true;
  if (import.meta.env.VITE_AUTH_DISABLED === 'false') return false;
  return import.meta.env.DEV;
}

/** App chrome + the single persistent WebSocket. Mounted once for the whole
 *  gated area so the socket survives /app ↔ /bunker/:id navigation. */
function AppShell({ children }: { children: ReactNode }) {
  useActivityWebSocket();
  return (
    <>
      <CrtOverlay />
      {children}
    </>
  );
}

/** Layout route guarding the authenticated app. */
function GatedLayout() {
  const { session } = useAuth();
  if (!session && !authBypassed()) return <LoginScreen />;
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

/** Shown for the few hundred ms the bunker chunk takes to arrive. */
function GateFallback() {
  return (
    <div className="min-h-screen bg-bunker-deep text-bunker-terminal font-terminal flex items-center justify-center">
      ▸ opening the vault door…
    </div>
  );
}

export function AppRoutes() {
  return (
    <>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/proof" element={<ProofPage />} />
        <Route element={<GatedLayout />}>
          <Route path="/app" element={<Suspense fallback={<GateFallback />}><HomePage /></Suspense>} />
          <Route path="/bunker/:id" element={<Suspense fallback={<GateFallback />}><BunkerPage /></Suspense>} />
        </Route>
      </Routes>
      {/* Outside the route table: reachable on the landing, on the login
          screen and inside the bunker alike. A visitor who bounces before
          signing up has no other way to tell us why. */}
      <FeedbackButton />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
